import type {FieldFunctionOptions, Reference} from '@apollo/client';
import {ApolloClient, HttpLink, InMemoryCache} from '@apollo/client';

function getMeta(name: string): string {
  if (typeof document !== 'undefined') {
    const csrfMeta = document.querySelector(`meta[name=${name}]`);
    if (csrfMeta) {
      const csrfToken = csrfMeta.getAttribute('content');
      if (csrfToken) {
        return csrfToken;
      }
    }
  }
  return '';
}

const CSRF_TOKEN = getMeta('csrf');
const ENDPOINT = getMeta('gql_endpoint');

// The language the page around us is in, so anything the server resolves
// through gettext -- error messages, most visibly -- comes back in it.
//
// `<html lang>` rather than `@pinecast/i18n`'s build locale: on the tip jar
// and the short-link pages the server picks the language from the *show*, not
// from the bundle, and it is the page the reader is looking at that the answer
// has to match. The server prefers this header over `Accept-Language` for
// exactly this reason (see `pinecast.i18n.language_from_headers`); a signed-in
// user's stored preference still outranks both.
function getDocumentLanguage(): string {
  if (typeof document === 'undefined') {
    return '';
  }
  return document.documentElement.lang || '';
}

const DOCUMENT_LANGUAGE = getDocumentLanguage();

const replace = (existing: any, incoming: any) => incoming;
const merge = (existing: any, incoming: any) => ({...existing, ...incoming});
const redirect =
  (typeName: string, argumentName: string) =>
  (
    existingData: any,
    options: FieldFunctionOptions<any, any>,
  ): Reference | undefined | null => {
    if (options.canRead(existingData)) {
      return existingData;
    }
    const extractedCache = options.cache.extract();
    if (!(`${typeName}:${options.args[argumentName]}` in extractedCache)) {
      return undefined;
    }
    return options.toReference({
      __typename: typeName,
      id: options.args?.[argumentName],
    });
  };

const client = new ApolloClient({
  link: new HttpLink({
    uri: ENDPOINT,
    credentials: 'include',
    headers: {
      'X-CSRFToken': CSRF_TOKEN,
      ...(DOCUMENT_LANGUAGE ? {'Content-Language': DOCUMENT_LANGUAGE} : {}),
    },
  }),
  cache: new InMemoryCache({
    dataIdFromObject: object => {
      switch (object.__typename) {
        case 'AdvertisementType':
        case 'NetworkType':
        case 'TagType':
          return (object as any).uuid;
        default:
          return undefined;
      }
    },
    typePolicies: {
      AppleSearchResult: {
        keyFields: ['serviceId'],
      },
      AssetType: {
        keyFields: ['uuid'],
      },
      PodcastType: {
        keyFields: ['slug'],
      },
      PublicPaidEpisodeInstanceType: {
        keyFields: ['episode', ['id']],
      },
      PublicPodcastEpisodeType: {
        keyFields: ['id'],
      },
      PublicPodcastType: {
        keyFields: ['slug'],
      },
      Query: {
        fields: {
          tipJarSubscription: redirect('SubscriptionType', 'podcastSlug'),
          tipJarEpisodeInstance: redirect(
            'PublicPaidEpisodeInstanceType',
            'episodeId',
          ),
        },
      },
      SelfUserType: {
        keyFields: false,
        fields: {
          allTags: {merge: replace},
        },
        merge,
      },
      SiteType: {
        merge,
      },
      UserFeatures: {
        keyFields: false,
      },
      UserType: {
        keyFields: ['email'],
        merge,
        fields: {
          allTags: {merge: replace},
        },
      },

      AssetUploadType: {
        keyFields: false,
      },
      Utterance: {
        keyFields: false,
      },
      ClipType: {
        keyFields: ['uuid'],
      },
      ClipPlacementType: {
        keyFields: false,
      },
    },
  }),
});

export default client;
